import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { MessageCircle, Send, X, AtSign } from "lucide-react";
import { useAuth } from "../lib/auth";

const ROLE_COL = { FOUNDER: "bg-emerald-100 text-emerald-800", BDOM: "bg-sky-100 text-sky-800", PCWM: "bg-violet-100 text-violet-800", GEHM: "bg-amber-100 text-amber-800", SMCM: "bg-pink-100 text-pink-800", PARTNER: "bg-slate-100 text-slate-700" };

/**
 * Floating chat widget mounted in the bottom-right corner of the app shell.
 * Type `@Name` to pick a recipient; every message is 1:1 and private.
 * Reuses the existing /api/chat endpoints (server-side ACL enforced).
 */
export default function ChatWidget() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [activeId, setActiveId] = useState("");
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionQ, setMentionQ] = useState("");
  const [totalUnread, setTotalUnread] = useState(0);
  const listEnd = useRef(null);
  const inputRef = useRef(null);

  const loadContacts = useCallback(async () => {
    try {
      const { data } = await api.get("/chat/contacts");
      setContacts(data || []);
      setTotalUnread((data || []).reduce((s, c) => s + (c.unread || 0), 0));
    } catch { /* offline / auth expiry — silently retry on next poll */ }
  }, []);
  useEffect(() => { loadContacts(); }, [loadContacts]);
  // Poll unread badge every 15s regardless of widget open state
  useEffect(() => { const t = setInterval(loadContacts, 15000); return () => clearInterval(t); }, [loadContacts]);

  const loadMessages = useCallback(async (pid) => {
    if (!pid) return;
    try { const { data } = await api.get(`/chat/messages?peer_id=${pid}`); setMessages(data || []); }
    catch { /* transient chat fetch failure — next open re-fetches */ }
  }, []);
  useEffect(() => { if (open && activeId) loadMessages(activeId); }, [open, activeId, loadMessages]);
  useEffect(() => {
    if (!open || !activeId) return;
    const t = setInterval(() => loadMessages(activeId), 6000);
    return () => clearInterval(t);
  }, [open, activeId, loadMessages]);
  useEffect(() => { listEnd.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const activeContact = useMemo(() => contacts.find(c => c.id === activeId), [contacts, activeId]);
  const mentionMatches = useMemo(() => {
    const q = mentionQ.toLowerCase();
    return contacts.filter(c => (c.name || "").toLowerCase().includes(q)).slice(0, 6);
  }, [contacts, mentionQ]);

  const onDraftChange = (e) => {
    const v = e.target.value; setDraft(v);
    const m = v.match(/@(\S*)$/);
    if (m) { setMentionQ(m[1]); setMentionOpen(true); }
    else { setMentionOpen(false); }
  };

  const chooseMention = (c) => {
    setActiveId(c.id);
    // Strip trailing @token from draft, replace with the resolved name for clarity.
    setDraft(prev => prev.replace(/@\S*$/, "").trimStart());
    setMentionOpen(false);
    inputRef.current?.focus();
  };

  const send = async () => {
    const text = draft.trim(); if (!text) return toast.error("Type a message");
    if (!activeId) return toast.error("Pick a recipient using @name");
    try {
      await api.post("/chat/messages", { peer_id: activeId, text });
      setDraft(""); await loadMessages(activeId); await loadContacts();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to send"); }
  };

  // Hide widget if user has no chat contacts (e.g. participant portal)
  if (!user || !contacts) return null;

  return (
    <>
      <button data-testid="chat-fab" onClick={() => setOpen(v => !v)}
              className="fixed right-4 bottom-4 z-40 bg-brand hover:bg-brand-dark text-white rounded-full w-14 h-14 shadow-lg flex items-center justify-center transition-transform hover:scale-105"
              title="Team Chat">
        <MessageCircle size={22}/>
        {totalUnread > 0 && (
          <span data-testid="chat-fab-badge" className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold min-w-[20px] h-5 px-1.5 rounded-full flex items-center justify-center border-2 border-white">
            {totalUnread > 99 ? "99+" : totalUnread}
          </span>
        )}
      </button>

      {open && (
        <div data-testid="chat-widget" className="fixed right-4 bottom-24 z-40 w-[360px] max-w-[calc(100vw-2rem)] bg-white border border-[color:var(--aii-border)] rounded-xl shadow-2xl flex flex-col overflow-hidden" style={{ maxHeight: "70vh", minHeight: 440 }}>
          <div className="p-3 border-b border-[color:var(--aii-border)] bg-[color:var(--aii-green-tint)] flex items-center gap-2">
            <MessageCircle size={16} className="text-brand"/>
            <div className="font-semibold text-sm flex-1">
              {activeContact ? (
                <>Chatting with <b>{activeContact.name}</b> <span className={`text-[9px] px-1 py-0.5 rounded uppercase font-bold ml-1 ${ROLE_COL[activeContact.role] || "bg-slate-100"}`}>{activeContact.role}</span></>
              ) : "Team Chat"}
            </div>
            <button onClick={() => setOpen(false)} className="text-[color:var(--aii-muted)] hover:text-[color:var(--aii-text)]"><X size={16}/></button>
          </div>

          {!activeContact && (
            <div className="p-3 border-b border-[color:var(--aii-border)] max-h-48 overflow-y-auto">
              <div className="text-[10px] uppercase tracking-wider text-[color:var(--aii-muted)] mb-1 flex items-center gap-1"><AtSign size={11}/> Choose contact</div>
              {contacts.length === 0 && <div className="text-xs italic text-[color:var(--aii-muted)] py-2">No contacts yet.</div>}
              {contacts.map(c => (
                <button key={c.id} data-testid={`fab-c-${c.id}`} onClick={() => setActiveId(c.id)}
                        className="w-full text-left px-2 py-1.5 rounded hover:bg-[color:var(--aii-green-tint)] flex items-center justify-between text-sm">
                  <div className="flex items-center gap-1.5">
                    <span className="font-semibold">{c.name}</span>
                    <span className={`text-[9px] px-1 py-0.5 rounded uppercase font-bold ${ROLE_COL[c.role] || "bg-slate-100"}`}>{c.role}</span>
                  </div>
                  {c.unread > 0 && <span className="bg-brand text-white text-[10px] px-1.5 rounded-full font-bold">{c.unread}</span>}
                </button>
              ))}
            </div>
          )}

          {activeContact && (
            <div className="flex-1 overflow-y-auto p-3 space-y-2" style={{ maxHeight: 360 }}>
              {messages.length === 0 && <div className="text-xs italic text-[color:var(--aii-muted)] text-center py-6">Say hello 👋</div>}
              {messages.map(m => {
                const mine = m.from_user_id === user.id;
                return (
                  <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm ${mine ? "bg-brand text-white rounded-br-sm" : "bg-[color:var(--aii-bg)] rounded-bl-sm"}`}>
                      {m.text}
                      <div className={`text-[9px] mt-1 ${mine ? "text-white/70" : "text-[color:var(--aii-muted)]"}`}>{m.created_at ? new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}</div>
                    </div>
                  </div>
                );
              })}
              <div ref={listEnd}/>
            </div>
          )}

          <div className="border-t border-[color:var(--aii-border)] p-2 relative">
            {mentionOpen && mentionMatches.length > 0 && (
              <div className="absolute left-2 right-2 bottom-full mb-1 bg-white border border-[color:var(--aii-border)] rounded-lg shadow-lg overflow-hidden" data-testid="chat-mention-menu">
                {mentionMatches.map(c => (
                  <button key={c.id} data-testid={`mention-${c.id}`} onClick={() => chooseMention(c)}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-[color:var(--aii-green-tint)] flex items-center gap-2">
                    <AtSign size={12} className="text-brand"/>
                    <span className="font-semibold">{c.name}</span>
                    <span className={`text-[9px] px-1 py-0.5 rounded uppercase font-bold ${ROLE_COL[c.role] || "bg-slate-100"}`}>{c.role}</span>
                  </button>
                ))}
              </div>
            )}
            <div className="flex gap-2 items-center">
              {activeContact && (
                <button onClick={() => { setActiveId(""); setMessages([]); }} className="text-[10px] px-2 py-1 border border-[color:var(--aii-border)] rounded text-[color:var(--aii-muted)] hover:bg-[color:var(--aii-bg)]" title="Switch contact">
                  <AtSign size={11}/>
                </button>
              )}
              <input ref={inputRef} data-testid="chat-fab-input" value={draft} onChange={onDraftChange}
                     onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                     placeholder={activeContact ? "Type a message… (Enter to send)" : "Type @name to pick a recipient…"}
                     className="flex-1 px-2 py-1.5 border border-[color:var(--aii-border)] rounded text-sm"/>
              <button data-testid="chat-fab-send" onClick={send} disabled={!draft.trim() || !activeId}
                      className="px-3 py-1.5 bg-brand text-white rounded hover:bg-brand-dark disabled:opacity-50 flex items-center gap-1"><Send size={12}/></button>
            </div>
            {!activeContact && (
              <div className="text-[10px] text-[color:var(--aii-muted)] mt-1">Tip: type <b>@</b> then any name (e.g. <code>@Kamal</code>). Messages are private — only that person sees them.</div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
